import { useCallback, useEffect, useMemo, useState } from 'react';

import { Form, Input, Select, TreeSelect } from 'antd';

import { useSearchLengthValidation } from '@/shared/hooks/useSearchLengthValidation';
import {
  DOMAIN_HIERARCHY_LIMIT,
  FORM_LABELS,
  FORM_PLACEHOLDERS,
  FORM_RULES,
  MODAL_WIDTHS,
  PLACEHOLDER_MESSAGES,
  VALIDATION_MESSAGES,
  VALIDATION_RULES,
} from '@/shared/lib/constants';
import { transformDomainHierarchyToTreeSelect } from '@/shared/lib/tree';
import { transformOwnersToSelectOptions } from '@/shared/lib/utils';
import { BaseModal } from '@/shared/ui/BaseModal';
import { DescriptionTextArea } from '@/shared/ui/DescriptionTextArea';
import { Switch } from '@/shared/ui/Switch';

import { useDisplaySettingsStore } from '@/entities/display-settings';
import { useDomainHierarchy } from '@/entities/domain';
import { useUserOwners } from '@/entities/owner';
import type { TagCategoryItem } from '@/entities/tags/domain';

import type { CreateClassificationRequest, UpdateClassificationRequest } from '@/features/tags/api';
import { useCreateClassification, useUpdateClassification } from '@/features/tags/model';
import styles from '@/features/tags/ui/_internal/modal.module.css';

type FormValues = {
  name: string;
  displayName?: string;
  description: string;
  mutuallyExclusive: boolean;
  owners?: string[];
  domains?: string;
};

type ClassificationFormModalProps = {
  open: boolean;
  mode?: 'create' | 'edit';
  classificationData?: TagCategoryItem;
  onClose: () => void;
  onSuccess?: (parentFqn: string) => void;
};

export const ClassificationFormModal = ({
  open,
  mode = 'create',
  classificationData,
  onClose,
  onSuccess,
}: ClassificationFormModalProps) => {
  const [form] = Form.useForm<FormValues>();
  const [domainSearchValue, setDomainSearchValue] = useState('');
  const createMutation = useCreateClassification();
  const updateMutation = useUpdateClassification(classificationData?.name);
  const showOwners = useDisplaySettingsStore((state) => state.showOwners);

  const { validateLength: validateDomainSearch } = useSearchLengthValidation({
    maxLength: 30,
    warningMessage: VALIDATION_MESSAGES.DOMAIN_SEARCH_MAX_LENGTH,
    debounceMs: 1000,
    shouldTruncate: true, // TreeSelect는 maxLength prop이 동작하지 않으므로 수동 truncate
  });

  const { data: domainHierarchy } = useDomainHierarchy({ limit: DOMAIN_HIERARCHY_LIMIT });
  const { data: usersData } = useUserOwners();

  const isLoading = createMutation.isPending || updateMutation.isPending;
  const isEditMode = mode === 'edit';

  const userOptions = useMemo(() => transformOwnersToSelectOptions(usersData), [usersData]);

  const modalTitle = useMemo(() => {
    if (isEditMode) {
      return `분류 수정 (${classificationData?.displayName || classificationData?.name || ''})`;
    }
    return '분류 추가';
  }, [isEditMode, classificationData]);

  const domainTreeData = useMemo(() => {
    if (!domainHierarchy?.data) return [];
    return transformDomainHierarchyToTreeSelect(domainHierarchy.data);
  }, [domainHierarchy]);

  useEffect(() => {
    if (open) {
      if (isEditMode && classificationData) {
        form.setFieldsValue({
          name: classificationData.name,
          displayName: classificationData.displayName || '',
          description: classificationData.description || '',
          mutuallyExclusive: classificationData.mutuallyExclusive || false,
          owners: classificationData.owners?.map((o) => o.id) || [],
          domains: classificationData.domains?.[0]?.fullyQualifiedName,
        });
      } else {
        form.resetFields();
        form.setFieldValue('mutuallyExclusive', false);
      }
    }
  }, [open, form, isEditMode, classificationData]);

  const handleClose = useCallback(() => {
    form.resetFields();
    onClose();
  }, [form, onClose]);

  const transformFormValuesToRequest = useCallback(
    (values: FormValues): CreateClassificationRequest | UpdateClassificationRequest => {
      const domains: string[] = values.domains ? [values.domains] : [];
      const owners = (values.owners || []).map((id) => ({ id, type: 'user' as const }));
      return {
        name: values.name,
        displayName: values.displayName || undefined,
        description: values.description || undefined,
        mutuallyExclusive: values.mutuallyExclusive,
        domains,
        owners,
      };
    },
    []
  );

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      try {
        const request = transformFormValuesToRequest(values);
        if (isEditMode && classificationData) {
          await updateMutation.mutateAsync(request);
          handleClose();
          onSuccess?.(classificationData.name);
        } else {
          await createMutation.mutateAsync(request);
          handleClose();
          onSuccess?.(values.name);
        }
      } catch (error) {
        console.error('Failed to submit classification form:', error);
      }
    },
    [
      isEditMode,
      classificationData,
      transformFormValuesToRequest,
      updateMutation,
      handleClose,
      onSuccess,
      createMutation,
    ]
  );

  return (
    <BaseModal
      title={modalTitle}
      open={open}
      onClose={handleClose}
      width={MODAL_WIDTHS.STANDARD_FORM}
      onOk={() => form.submit()}
      okText={isEditMode ? '저장' : '생성'}
      confirmLoading={isLoading}
      styles={{ body: { minHeight: 520, maxHeight: 800 } }}>
      <Form form={form} layout="vertical" onFinish={handleSubmit} className={styles.form}>
        <Form.Item
          name="name"
          label={FORM_LABELS.NAME}
          rules={[...FORM_RULES.REQUIRED_NAME, ...FORM_RULES.TAG_CATEGORY_NAME]}
          validateFirst>
          <Input placeholder={FORM_PLACEHOLDERS.NAME} disabled={isEditMode} allowClear />
        </Form.Item>

        <Form.Item name="displayName" label={FORM_LABELS.DISPLAY_NAME}>
          <Input placeholder={FORM_PLACEHOLDERS.DISPLAY_NAME} allowClear />
        </Form.Item>

        <Form.Item
          name="description"
          label={FORM_LABELS.DESCRIPTION}
          rules={FORM_RULES.REQUIRED_DESCRIPTION}
          validateTrigger={['onSubmit']}>
          <DescriptionTextArea
            placeholder={FORM_PLACEHOLDERS.DESCRIPTION}
            showHelper
            maxLength={VALIDATION_RULES.TAG_DESCRIPTION_MAX_LENGTH}
          />
        </Form.Item>

        {!isEditMode && (
          <Form.Item name="mutuallyExclusive" label={FORM_LABELS.MUTUALLY_EXCLUSIVE} valuePropName="checked">
            <Switch aria-label={FORM_LABELS.MUTUALLY_EXCLUSIVE} color="standard" />
          </Form.Item>
        )}

        <Form.Item name="domains" label={FORM_LABELS.DOMAINS}>
          <TreeSelect
            placeholder={PLACEHOLDER_MESSAGES.DOMAIN_SELECT}
            allowClear
            treeData={domainTreeData}
            treeDefaultExpandAll
            treeLine
            searchValue={domainSearchValue}
            onSearch={(value) => {
              const validatedValue = validateDomainSearch(value);
              setDomainSearchValue(validatedValue);
            }}
          />
        </Form.Item>

        {showOwners && (
          <Form.Item name="owners" label={FORM_LABELS.OWNERS}>
            <Select mode="multiple" placeholder={PLACEHOLDER_MESSAGES.OWNER_SELECT} allowClear options={userOptions} />
          </Form.Item>
        )}
      </Form>
    </BaseModal>
  );
};
